<template>
  <Suspense>
    <div class="app">
      <div id="app-top-anchor"></div>
      <PaymentHoldModal />
      <RouterView />
      <AppCartSidebar />
      <PortfolioTrackerModal />
      <PackageBuilderModal />
      <DevDrawer v-if="devToolsEnabled" />
    </div>
    <template #fallback> {{ commonContent.loadingLabel }} </template>
  </Suspense>
</template>

<script lang="ts">
import { defineAsyncComponent, defineComponent } from 'vue'
import { useToast } from 'vue-toastification'
import { RouterView, useRouter } from 'vue-router'

import { useAppContentStore } from '@/stores/appContent'
import { useCartStore } from '@/stores/cart'
import { useMainStore } from '@/stores/main'
import { mapState, mapStores } from 'pinia'

import {
  isLeavingCompletedOrder,
  leaveCompletedOrder,
  takeOrderCompletedNotice
} from '@/composables/completedOrder'
import { useEnhancedFlyout } from '@/composables/useEnhancedFlyout'
import { useRewardThresholdCrossing } from '@/composables/useRewardThresholdCrossing'
import { useRewardToasts } from '@/composables/useRewardToasts'
import { CrossTabMessageType, onCrossTabMessage, postCrossTabMessage } from '@/lib/crossTabChannel'
import { DEV_TOOLS_ENABLED } from '@/lib/devToolsAccess'
import { tabHoldsPayment } from '@/lib/paymentHoldMarker'

import PackageBuilderModal from '@/components/Senior/PackageBuilder/PackageBuilderModal.vue'
import PortfolioTrackerModal from '@/components/Senior/PortfolioTracker/PortfolioTrackerModal.vue'
import AppCartSidebar from '@/layout/AppCartSidebar/AppCartSidebar.vue'
import PaymentHoldModal from '@/layout/PaymentHoldModal.vue'

import { CadyMarket } from '@/types/CadyMarket'

import { commonContent } from '@/content/common'
import { feedbackContent } from '@/content/feedback'

export default defineComponent({
  name: 'App',
  setup() {
    // Fire Added / Removed reward toasts when a cart mutation crosses a tier threshold (app-level so
    // it listens across every page). The composable baselines silently, so no toast on load.
    //
    // Suppressed where the enhanced flyout is active (Gap 11): there the flyout opens and plays the
    // unlock sequence, which is the celebration — a gradient toast over the top would compete with it.
    // The gate is read at fire time rather than at wiring time, so it follows the customer's route.
    //
    // Detection stays wired on every route either way. It is the *rendering* that is gated, which is
    // what lets A2 hang the sequence off the same crossing without a second detector.
    const { showAddedToast, showRemovedToast } = useRewardToasts()
    const { isEnhancedFlyoutActive } = useEnhancedFlyout()

    useRewardThresholdCrossing({
      onAdded: (crossing) => {
        if (!isEnhancedFlyoutActive.value) showAddedToast(crossing)
      },
      onRemoved: (crossing) => {
        if (!isEnhancedFlyoutActive.value) showRemovedToast(crossing)
      }
    })

    return {
      commonContent,
      devToolsEnabled: DEV_TOOLS_ENABLED,
      router: useRouter(),
      toast: useToast()
    }
  },
  components: {
    AppCartSidebar,
    // Registered in dev and demo builds only, so production builds leave the drawer out.
    ...(DEV_TOOLS_ENABLED && {
      DevDrawer: defineAsyncComponent({
        loader: () => import('@/components/Shared/DevDrawer/DevDrawer.vue'),
        suspensible: false
      })
    }),
    PackageBuilderModal,
    PaymentHoldModal,
    PortfolioTrackerModal,
    RouterView
  },
  data() {
    return {
      stopCrossTabReload: null as (() => void) | null,
      recheckPaymentHold: null as (() => void) | null
    }
  },
  computed: {
    ...mapStores(useMainStore, useAppContentStore, useCartStore),
    ...mapState(useMainStore, ['cadyMarket']),
    ...mapState(useCartStore, ['cartData'])
  },
  beforeMount() {
    console.log('Version: ' + __APP_VERSION__)

    this.stopCrossTabReload = onCrossTabMessage((type, cartId) => {
      // A tab leaving for the landing would have that cancelled by a reload. The receipt and the
      // Stripe return page work from their URL, and moving them takes the customer off their order.
      if (isLeavingCompletedOrder() || this.$route.meta.ignoresOtherTabs) return

      if (type === CrossTabMessageType.OrderCompleted) {
        this.leaveCompletedOrderOf(cartId)
      } else if (type === CrossTabMessageType.CartUpdated) {
        this.router.go(0)
      } else if (type === CrossTabMessageType.SessionChanged) {
        // Home resumes the new session on its own market's landing. Reloading in place would keep
        // this tab's route: another market's, one that loads nothing, or a stale `?s=`.
        window.location.href = '/'
      }
    })
  },
  mounted() {
    document.addEventListener('contextmenu', this.handleContextMenuEvent)

    if (takeOrderCompletedNotice()) {
      this.toast.info(feedbackContent.orderCompletedInAnotherTab, { timeout: 10000 })
    }

    // A tab showing a held cart re-checks it whenever it comes back into view, so a hold that ended
    // elsewhere (released in the paying tab, or expired) doesn't leave it frozen. On load, the cart
    // store's `revalidatePersistedCart` already refetches it.
    this.recheckPaymentHold = () => {
      if (document.visibilityState === 'visible') void this.cartStore.recheckPaymentHold()
    }
    document.addEventListener('visibilitychange', this.recheckPaymentHold)
    window.addEventListener('focus', this.recheckPaymentHold)

    // Load the universal Rewards Tracker content module (heading templates) once
    // per session, ungated by market. Fire-and-forget — the action handles its
    // own errors and falls back to in-code headings.
    void this.appContentStore.loadRewardsTrackerContent()
  },
  beforeUnmount() {
    document.removeEventListener('contextmenu', this.handleContextMenuEvent)
    if (this.recheckPaymentHold) {
      document.removeEventListener('visibilitychange', this.recheckPaymentHold)
      window.removeEventListener('focus', this.recheckPaymentHold)
    }

    this.stopCrossTabReload?.()
  },
  methods: {
    /**
     * Another tab completed the order on `cartId` and has cleared the saved stores, so a tab showing
     * that cart leaves for its landing rather than reloading into nothing. Left alone: the tab paying
     * for it.
     */
    leaveCompletedOrderOf(cartId: string | null) {
      if (!cartId || cartId !== this.cartData?.cartId) return
      if (tabHoldsPayment(this.cartData?.order?.orderId)) return

      leaveCompletedOrder(this.router, { clear: false })
    },
    handleContextMenuEvent: (event) => {
      const target = event.target as HTMLElement
      if (target.tagName === 'IMG') {
        event.preventDefault()
      }
    }
  },
  watch: {
    cartData: {
      handler() {
        if (document.hasFocus()) {
          postCrossTabMessage(CrossTabMessageType.CartUpdated)
        }
      },
      immediate: true,
      deep: true
    },
    // Load the Senior-ordering Contentful modules once the market resolves to
    // Senior. The two app-root modals (Portfolio Tracker, Package Builder) and
    // the shared-header Portfolio link sit above any single route, so the load
    // lives here rather than in a route-scoped loader (ADR 2026.06.08-004).
    cadyMarket: {
      handler(market: CadyMarket | null) {
        if (market === CadyMarket.Senior) {
          // Fire-and-forget: the action handles its own errors internally.
          void this.appContentStore.loadSeniorOrderingContent()
        }
      },
      immediate: true
    }
  }
})
</script>

<style lang="scss" scoped>
.app {
  background-color: #fff;
}
</style>
